import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, GeoJSON } from 'react-leaflet'; 
import api from './api';
import { 
  Box, Typography, Container, Button, List, ListItem, ListItemText, 
  IconButton, Tooltip, Alert, AlertTitle, Snackbar, ListItemIcon, Paper,
  Accordion, AccordionSummary, AccordionDetails // <-- NEW IMPORTS
} from '@mui/material'; 
import DeleteIcon from '@mui/icons-material/Delete'; 
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'; // <-- NEW IMPORT
import christmasBg from './assets/christmas-bg.jpg'; 
import MapEvents from './MapEvents'; 
import PhotoGallery from './PhotoGallery'; 
import FullscreenButton from './FullscreenButton'; 
import MapLegend from './MapLegend'; 
import SuburbSearch from './SuburbSearch';
import VisitorCounter from './VisitorCounter';

import MyLocationIcon from '@mui/icons-material/MyLocation';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import UndoIcon from '@mui/icons-material/Undo';
import NavigationIcon from '@mui/icons-material/Navigation';
import CloudUploadIcon from '@mui/icons-material/CloudUpload'; 
import DragHandleIcon from '@mui/icons-material/DragHandle';   
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AddHomeIcon from '@mui/icons-material/AddHome';

import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import Confetti from 'react-confetti'; 
import { redIcon, greenIcon, blueIcon, orangeIcon, yellowIcon, fireworksIcon } from './icons';
import { useNavigate, Link as RouterLink } from 'react-router-dom';

const postcodeStyle = {
  color: "#1E90FF",       
  weight: 1,              
  opacity: 0.5,           
  fillOpacity: 0.1        
};

const fireworksLocations = [
  { id: 'fw1', name: 'Sydney Harbour', lat: -33.8568, lng: 151.2153, time: '9pm & Midnight' },
  { id: 'fw2', name: 'Melbourne CBD (Docklands)', lat: -37.8150, lng: 144.9460, time: '9:30pm & Midnight' },
  { id: 'fw3', name: 'Brisbane (South Bank)', lat: -27.4772, lng: 153.0233, time: '8pm & Midnight' },
  { id: 'fw4', name: 'Perth (Elizabeth Quay)', lat: -31.9566, lng: 115.8575, time: '8pm & Midnight' },
  { id: 'fw5', name: 'Adelaide (Elder Park)', lat: -34.9196, lng: 138.5997, time: '9pm & Midnight' },
  { id: 'fw6', name: 'Geelong Waterfront', lat: -38.1439, lng: 144.3644, time: '9:30pm & Midnight' },
  { id: 'fw7', name: 'Ballarat (Lake Wendouree)', lat: -37.5501, lng: 143.8340, time: '9:30pm' },
];

function HomePage() {
  const [displays, setDisplays] = useState([]);
  const [map, setMap] = useState(null); 
  const [myPosition, setMyPosition] = useState(null); 
  const [waypoints, setWaypoints] = useState([]);
  const navigate = useNavigate(); 

  const [mySubmissions, setMySubmissions] = useState([]);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [selectedDisplay, setSelectedDisplay] = useState(null);

  const [visibility, setVisibility] = useState({
    public: true,
    myCompleted: true,
    myPending: true,
    route: true,
    myLocation: true,
    postcodes: false,
    fireworks: true,
  });
  
  const [postcodeData, setPostcodeData] = useState(null);
  const [postcodeLookup, setPostcodeLookup] = useState({});

  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  const [visitedIds, setVisitedIds] = useState([]);
  const [showConfetti, setShowConfetti] = useState(false);

  const mapCenter = [-25.2744, 133.7751]; 

  const handleMarkVisited = (id) => {
    if (!visitedIds.includes(id)) {
      setVisitedIds([...visitedIds, id]);
      setShowConfetti(true);
      setTimeout(() => setShowConfetti(false), 5000);
    }
  };

  useEffect(() => {
    const fetchVerifiedDisplays = async () => {
      try {
        const response = await api.get('/displays/verified');
        setDisplays(response.data); 
      } catch (err) {
        console.error("Error fetching displays:", err);
      }
    };
    fetchVerifiedDisplays();

    const token = localStorage.getItem('token');
    if (token) {
      const fetchUserData = async () => {
        try {
          const subsResponse = await api.get('/displays/me'); 
          setMySubmissions(subsResponse.data);
          const routeResponse = await api.get('/route');
          if (routeResponse.data && routeResponse.data.length > 0) {
            setWaypoints(routeResponse.data);
            setToastMessage('Route loaded from cloud!');
            setToastOpen(true);
          }
        } catch (err) {
          console.error("Could not fetch user data", err);
        }
      };
      fetchUserData();
    }
  }, []); 

  useEffect(() => {
    fetch('/postcodes.geojson') 
      .then(response => response.json())
      .then(data => setPostcodeData(data))
      .catch(err => console.error("Error fetching postcode geojson:", err));

    fetch('/PostcodeData-final.txt')
      .then(response => response.text())
      .then(text => {
        const lookup = {};
        const lines = text.split('\n');
        lines.forEach(line => {
          const parts = line.split(',');
          if (parts.length > 2) {
            const suburb = parts[0].trim().toUpperCase(); 
            const postcode = parts[2].trim();             
            lookup[suburb] = postcode;
          }
        });
        setPostcodeLookup(lookup);
      })
      .catch(err => console.error("Error fetching postcode text:", err));
  }, []); 


  const handleVisibilityToggle = (type) => {
    setVisibility(prev => ({...prev, [type]: !prev[type]}));
  };

  const findMyLocal = () => { if (map) map.locate(); };

  const clearWaypoints = () => {
    if(window.confirm("Are you sure you want to clear your route?")) {
      setWaypoints([]);
    }
  };

  const removeLastWaypoint = () => { setWaypoints(waypoints.slice(0, -1)); };

  const removeWaypointAtIndex = (indexToRemove) => {
    setWaypoints(waypoints.filter((_, index) => index !== indexToRemove));
  };

  const handleSaveRoute = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/add'); 
      return;
    }
    try {
      await api.put('/route', { waypoints });
      setToastMessage('Route saved successfully!');
      setToastOpen(true);
    } catch (err) {
      console.error(err);
      alert("Failed to save route.");
    }
  };

  const onDragEnd = (result) => {
    if (!result.destination) return;
    const items = Array.from(waypoints);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    setWaypoints(items);
  };

  const handleStartDrive = () => {
    let routePoints = [];
    if (myPosition) routePoints.push(`${myPosition.lat},${myPosition.lng}`);
    waypoints.forEach(pos => routePoints.push(`${pos.lat},${pos.lng}`));

    if (routePoints.length < 1) return; 

    const baseUrl = 'https://www.google.com/maps/dir/';
    if (routePoints.length === 1) {
      window.open(`${baseUrl}?api=1&destination=${routePoints[0]}`, '_blank');
      return;
    }
    const origin = routePoints.shift(); 
    const destination = routePoints.pop(); 
    let url = `${baseUrl}?api=1&origin=${origin}&destination=${destination}`;
    if (routePoints.length > 0) {
      const intermediateWaypoints = routePoints.join('|');
      url += `&waypoints=${intermediateWaypoints}`;
    }
    window.open(url, '_blank');
  };

  const handleOpenGallery = (display) => { setSelectedDisplay(display); setGalleryOpen(true); };
  const handleCloseGallery = () => { setGalleryOpen(false); setSelectedDisplay(null); };
  
  const handleViewPhotosFromRoute = (waypoint) => {
    const displayData = { address: waypoint.name, photos: waypoint.photos || [] };
    handleOpenGallery(displayData);
  };

  const handlePhotoDeleted = (deletedPhotoId) => {
    setDisplays(prev => prev.map(d => d.id === selectedDisplay.id ? { ...d, photos: d.photos.filter(p => p.id !== deletedPhotoId) } : d));
    setMySubmissions(prev => prev.map(d => d.id === selectedDisplay.id ? { ...d, photos: d.photos.filter(p => p.id !== deletedPhotoId) } : d));
    setSelectedDisplay(prev => ({ ...prev, photos: prev.photos.filter(p => p.id !== deletedPhotoId) }));
  };

  const onEachPostcode = (feature, layer) => {
    const p = feature.properties;
    const suburbRaw = p.vic_loca_2 || p.nsw_loca_2 || p.qld_loca_2 || p.sa_loca_2 || p.wa_local_2 || p.tas_loca_2 || p.nt_loca_2 || p.act_loca_2;
    if (suburbRaw) {
      const properName = suburbRaw.toLowerCase().replace(/\b\w/g, s => s.toUpperCase());
      const suburbKey = suburbRaw.toUpperCase();
      const postcode = postcodeLookup[suburbKey];
      if (postcode) {
        layer.bindTooltip(`${properName}, ${postcode}`);
      } else {
        layer.bindTooltip(properName);
      }
    }
  };

  const myDisplayIds = mySubmissions.map(d => d.id);

  const addWaypoint = (lat, lng, name, photos = [], id) => {
    const newWaypoint = { lat, lng, name, photos, id, uuid: `${id}-${Date.now()}-${Math.random()}` };
    setWaypoints(prev => [...prev, newWaypoint]);
  };

  // --- SEO FAQ SCHEMA ---
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [{
      "@type": "Question",
      "name": "Where are the best Christmas lights near me?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "You can find the best Christmas lights in 2025 using our interactive map. We feature verified displays in Melbourne, Sydney, Brisbane, Perth, Ballarat, and more."
      }
    }, {
      "@type": "Question",
      "name": "How do I plan a Christmas lights driving route?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Use our map to click on displays you want to visit. They will be added to your route list. Then click 'Start Navigation' to send the optimized route directly to Google Maps."
      }
    }, {
      "@type": "Question",
      "name": "Is this Christmas lights map free?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes, viewing the map and planning routes is 100% free for the community."
      }
    }]
  };

  return (
    <Box sx={{ minHeight: 'calc(100vh - 64px)', backgroundImage: `url(${christmasBg})`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundAttachment: 'fixed', padding: '2rem 0' }}>
      
      {/* Inject Schema */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />

      {showConfetti && (
        <Confetti width={window.innerWidth} height={window.innerHeight} numberOfPieces={400} gravity={0.2} recycle={false} onConfettiComplete={() => setShowConfetti(false)} style={{ position: 'fixed', top: 0, left: 0, zIndex: 2000 }} />
      )}

      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', backgroundColor: 'rgba(255, 255, 255, 0.9)', padding: { xs: '1rem', md: '2rem' }, borderRadius: '8px', boxShadow: '0 8px 24px rgba(0,0,0,0.1)' }}>

          <Alert severity="info" variant="filled" sx={{ width: '100%', mb: 3 }}>
            <AlertTitle sx={{ fontWeight: 'bold' }}>Make a free account, Register your Christmas Lights Display & Make it visible to the Community!</AlertTitle>
          </Alert>
          
          <Typography component="h1" variant="h3" gutterBottom sx={{ textAlign: 'center', fontWeight: 'bold' }}>LIVE! Christmas Lights Map for 2025</Typography>
          {/* --- ADD COUNTER HERE --- */}
          <VisitorCounter />
          {/* ------------------------ */}
          <Typography component="h2" variant="h5" gutterBottom sx={{ textAlign: 'center' }}>Find the Best Houses Near You (Australia)</Typography>
          
          <SuburbSearch />
          
          <Typography variant="body1" sx={{ textAlign: 'center', mb: 2 }}>
            The Ultimate community-driven Australian and World Wide Christmas Lights Map for 2025. 
            Discover the top-rated displays in Sydney, Melbourne, Ballarat, and a long list of other popular cities.
          </Typography>

          <Box sx={{ width: '100%', mb: 2, display: { xs: 'none', md: 'flex' }, flexWrap: 'wrap', gap: 2 }}>
            <Button variant="contained" onClick={findMyLocal}>Find My Location</Button>
            <Button variant="outlined" color="secondary" onClick={clearWaypoints} disabled={waypoints.length === 0 && !myPosition}>Clear Route</Button>
            <Button variant="outlined" color="success" onClick={handleSaveRoute} startIcon={<CloudUploadIcon />}>Save Route</Button>
            <Button variant="outlined" color="warning" onClick={removeLastWaypoint} disabled={waypoints.length === 0}>Remove Last Stop</Button>
{/* NEW ADD DISPLAY BUTTON */}
  <Button 
    variant="contained" 
    color="error" 
    component={RouterLink} 
    to="/add" 
    startIcon={<AddHomeIcon />}
  >
    Add My Display
  </Button>
            <Button variant="contained" color="primary" onClick={handleStartDrive} disabled={waypoints.length === 0 && !myPosition} sx={{ ml: 'auto' }}>Start Navigation</Button>
          </Box>

          <Typography variant="caption" sx={{ display: { xs: 'block', md: 'none' }, textAlign: 'center', mb: 1, fontStyle: 'italic' }}>Hold finger on icon to display function</Typography>
          <Box sx={{ width: '100%', mb: 2, display: { xs: 'flex', md: 'none' }, justifyContent: 'space-between', alignItems: 'center' }}>
            <Tooltip title="Find My Location" placement="top"><IconButton color="primary" variant="contained" onClick={findMyLocal}><MyLocationIcon /></IconButton></Tooltip>
            <Tooltip title="Clear Route" placement="top"><span><IconButton color="secondary" onClick={clearWaypoints} disabled={waypoints.length === 0 && !myPosition}><DeleteForeverIcon /></IconButton></span></Tooltip>
            <Tooltip title="Save Route" placement="top"><span><IconButton color="success" onClick={handleSaveRoute}><CloudUploadIcon /></IconButton></span></Tooltip>
            <Tooltip title="Remove Last Stop" placement="top"><span><IconButton color="warning" onClick={removeLastWaypoint} disabled={waypoints.length === 0}><UndoIcon /></IconButton></span></Tooltip>
            {/* NEW ADD DISPLAY ICON */}
  <Tooltip title="Add My Display" placement="top">
    <IconButton color="error" component={RouterLink} to="/add">
      <AddHomeIcon />
    </IconButton>
  </Tooltip>
            <Tooltip title="Start Navigation" placement="top"><span><IconButton color="primary" variant="contained" onClick={handleStartDrive} disabled={waypoints.length === 0 && !myPosition}><NavigationIcon /></IconButton></span></Tooltip>
          </Box>
          
          {/* MAP BOX */}
          <Box sx={{ height: { xs: '400px', md: '600px' }, width: '100%', border: '1px solid #ddd', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)', position: 'relative' }}>
            <MapContainer center={mapCenter} zoom={4} style={{ height: '100%', width: '100%', borderRadius: '8px' }} ref={setMap}>
              <MapEvents setWaypoints={setWaypoints} setMyPosition={setMyPosition} />
              <TileLayer attribution='© OpenStreetMap contributors' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
              <FullscreenButton />
              <MapLegend visibility={visibility} onToggle={handleVisibilityToggle} />
              {visibility.postcodes && postcodeData && <GeoJSON data={postcodeData} style={postcodeStyle} onEachFeature={onEachPostcode} />}
              
              {/* MARKERS CODE (Keep existing) */}
              {visibility.public && displays.filter(display => !myDisplayIds.includes(display.id)).map(display => (
                <Marker key={display.id} position={[display.latitude, display.longitude]} icon={redIcon} eventHandlers={{ click: () => addWaypoint(parseFloat(display.latitude), parseFloat(display.longitude), `${display.first_name}'s Display`, display.photos, display.id) }}>
                  <Popup>
                    <strong>{display.first_name}'s Display</strong><br />{display.address}<br />
                    <Button size="small" onClick={() => handleOpenGallery(display)} sx={{ p: 0, mt: 1, textTransform: 'none' }}>View Photos ({display.photos.length})</Button>
                    <Button variant={visitedIds.includes(display.id) ? "outlined" : "contained"} color={visitedIds.includes(display.id) ? "success" : "primary"} size="small" startIcon={visitedIds.includes(display.id) ? <CheckCircleIcon /> : null} sx={{ mt: 1, width: '100%' }} onClick={() => handleMarkVisited(display.id)}>{visitedIds.includes(display.id) ? "Visited!" : "Mark as Visited"}</Button>
                  </Popup>
                </Marker>
              ))}
              {/* PLACE THE FIREWORKS CODE HERE */}
{visibility.fireworks && fireworksLocations.map(fw => (
  <Marker key={fw.id} position={[fw.lat, fw.lng]} icon={fireworksIcon}>
    <Popup>
      <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>🎆 {fw.name} NYE Fireworks</Typography>
      <Typography variant="body2">Times: {fw.time}</Typography>
      <Button size="small" component={RouterLink} to="/blog/new-years-eve-fireworks-2025" sx={{ mt: 1 }}>
        View Event Guide
      </Button>
    </Popup>
  </Marker>
))}
              {mySubmissions.map(display => {
                const isCompleted = display.payment_status === 'completed';
                if ((isCompleted && !visibility.myCompleted) || (!isCompleted && !visibility.myPending)) return null;
                const icon = isCompleted ? yellowIcon : orangeIcon;
                return (
                  <Marker key={display.id} position={[parseFloat(display.latitude), parseFloat(display.longitude)]} icon={icon} eventHandlers={{ click: () => addWaypoint(parseFloat(display.latitude), parseFloat(display.longitude), 'My Submission', display.photos || [], display.id) }}>
                    <Popup>
                        <strong>My Submission</strong><br />{display.address}<br /><em>Status: {display.payment_status}</em><br />
                        {display.photos && display.photos.length > 0 && <Button size="small" onClick={() => handleOpenGallery(display)} sx={{ p: 0, mt: 1, textTransform: 'none' }}>View Photos ({display.photos.length})</Button>}
                        <Button variant={visitedIds.includes(display.id) ? "outlined" : "contained"} color={visitedIds.includes(display.id) ? "success" : "primary"} size="small" startIcon={visitedIds.includes(display.id) ? <CheckCircleIcon /> : null} sx={{ mt: 1, width: '100%' }} onClick={() => handleMarkVisited(display.id)}>{visitedIds.includes(display.id) ? "Visited!" : "Mark as Visited"}</Button>
                    </Popup>
                  </Marker>
                );
              })}
              {visibility.myLocation && myPosition && <Marker position={myPosition} icon={blueIcon}><Popup>You are here!</Popup></Marker>}
              {visibility.route && waypoints.map((pos, idx) => <Marker key={idx} position={[pos.lat, pos.lng]} icon={greenIcon}><Popup>{pos.name ? pos.name : `Stop ${idx + 1}`}</Popup></Marker>)}
            </MapContainer>
          </Box>

          <Box sx={{ width: '100%', mt: 4, textAlign: 'center' }}>
            <Typography variant="h5" gutterBottom>A Community-Powered Map</Typography>
            {/* --- ADD IT HERE --- */}
            <iframe 
                src="https://www.noradsanta.org/countdownClock.html" 
                title="NTS Countdown Clock"
                style={{ width: '100%', maxWidth: '400px', height: '232px', border: 'none', marginBottom: '20px' }}
            ></iframe>
            {/* ------------------- */}
            <Box component="img" src="/share-image.png" alt="Map of Christmas light displays in Australia" sx={{ width: '150px', height: 'auto', borderRadius: '8px', my: 2, border: '1px solid #eee' }} />
            <Typography variant="body1" paragraph>This map is built by people just like you! Every year, hundreds of Australians create magical displays. Use our map to find the best festive houses in your area, or click "Add Display" to share your own work with the community.</Typography>
            <Typography variant="body1" paragraph>From giant inflatable Santas in <strong>Ballarat</strong> to entire streets lit up in <strong>Sydney</strong>, our 2025 map is your best guide to the season.</Typography>
            <Typography variant="h6" component="p" color="primary" sx={{ mt: 3, fontStyle: 'italic', fontWeight: 'bold' }}>Help spread the family festivities and inspiration - Light up our map for Christmas and the Community!</Typography>
          </Box>

          {/* --- FAQ SECTION FOR SEO --- */}
          <Box sx={{ width: '100%', mt: 6, textAlign: 'left' }}>
            <Typography variant="h5" gutterBottom sx={{ textAlign: 'center', mb: 3 }}>Frequently Asked Questions</Typography>
            
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6">Where are the best Christmas lights near me?</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography>
                  You can use our interactive <strong>Christmas Lights Map 2025</strong> to find verified displays in your local area. 
                  Simply click "Find My Location" to see houses near you, or use the search bar to check specific suburbs like 
                  Wendouree, Martin Place, or Patterson Lakes.
                </Typography>
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6">How do I plan a Christmas light driving route?</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography>
                  It's easy! Click on any red pin on the map to add it to your "Route List." 
                  You can drag and drop to reorder the stops. When you are ready, click <strong>Start Navigation</strong> to 
                  send the entire route directly to Google Maps on your phone for turn-by-turn directions.
                </Typography>
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6">Is this map free to use?</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography>
                  Yes! Viewing the map, planning routes, and using the navigation features is 100% free for the community. 
                  We rely on small fees from display owners to keep the servers running.
                </Typography>
              </AccordionDetails>
            </Accordion>
             
             <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6">How do I add my house to the map?</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography>
                  If you have a display, we'd love to feature it! Click "Add Display" in the menu, enter your address, 
                  and upload your photos. Verified displays get a red pin and can be seen by thousands of locals.
                </Typography>
              </AccordionDetails>
            </Accordion>

          </Box>

          {/* Route List */}
          {waypoints.length > 0 && (
            <Box sx={{ width: '100%', mt: 3 }}> 
              <Typography variant="h6">My Route (Drag to Reorder):</Typography>
              <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="route-list">
                  {(provided) => (
                    <List dense {...provided.droppableProps} ref={provided.innerRef}>
                      {waypoints.map((pos, idx) => {
                        const uniqueKey = pos.uuid || `item-${idx}`;
                        return (
                          <Draggable key={uniqueKey} draggableId={uniqueKey} index={idx}>
                            {(provided) => (
                              <Paper ref={provided.innerRef} {...provided.draggableProps} elevation={1} sx={{ mb: 1, backgroundColor: '#fff' }}>
                                <ListItem secondaryAction={
                                    <Box>
                                      {pos.photos && <IconButton edge="end" onClick={() => handleViewPhotosFromRoute(pos)}><PhotoLibraryIcon /></IconButton>}
                                      <IconButton edge="end" onClick={() => removeWaypointAtIndex(idx)}><DeleteIcon /></IconButton>
                                    </Box>
                                  }>
                                  <ListItemIcon {...provided.dragHandleProps} sx={{ minWidth: '30px', cursor: 'grab' }}><DragHandleIcon /></ListItemIcon>
                                  <ListItemText primary={pos.name && pos.name !== 'Custom Waypoint' ? `Stop ${idx + 1}: ${pos.name}` : `Stop ${idx + 1}`} secondary={`(${pos.lat.toFixed(4)}, ${pos.lng.toFixed(4)})`} />
                                </ListItem>
                              </Paper>
                            )}
                          </Draggable>
                        );
                      })}
                      {provided.placeholder}
                    </List>
                  )}
                </Droppable>
              </DragDropContext>
            </Box>
          )}
        </Box>

        {selectedDisplay && (
          <PhotoGallery open={galleryOpen} onClose={handleCloseGallery} photos={selectedDisplay.photos || []} displayName={selectedDisplay.address} onPhotoDeleted={myDisplayIds.includes(selectedDisplay.id) ? handlePhotoDeleted : null} />
        )}

        <Snackbar open={toastOpen} autoHideDuration={3000} onClose={() => setToastOpen(false)} message={toastMessage} />
      </Container>
    </Box>
  );
}

export default HomePage;